import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../ui/Modal';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { LogOut, Bell, Cloud, Shield } from 'lucide-react';

interface SignOutConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export function SignOutConfirmModal({ isOpen, onClose, onConfirm }: SignOutConfirmModalProps) {
  const [isSigningOut, setIsSigningOut] = useState(false);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await onConfirm();
      onClose();
      // Redirect to home page after successful sign-out
      navigate('/');
    } catch (error) {
      console.error('Sign out failed:', error);
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <Card className="border-0 shadow-none">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-amber-100 rounded-full">
              <LogOut className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Sign Out?</h3>
              <p className="text-sm text-muted-foreground">
                You'll need to sign back in to continue
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <p className="text-sm font-medium">While signed out, you won't be able to:</p>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                Access GodMode productivity features
              </li>
              <li className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-muted-foreground" />
                Receive schedule notifications
              </li>
              <li className="flex items-center gap-2">
                <Cloud className="h-4 w-4 text-muted-foreground" />
                Sync data across devices
              </li>
            </ul>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={isSigningOut}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleSignOut}
              disabled={isSigningOut}
            >
              {isSigningOut ? 'Signing Out...' : 'Sign Out'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </Modal>
  );
}
